import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../../api';
import { useCan } from '../../auth';
import { fmtNum, Notice, PageHead, Q, Section, Tag } from '../../ui/kit';
import { CATEGORY_INFO, NOT_APPLIED, PolicyLens, SaveBar, ThreatOffBanner, categoryLabel, lines, threatOff, useProtectionSettings, useSaveProtection, type ProtectionSettingsResponse, type SaveResult } from './common';

/**
 * Protect → Threat protection → Web (P4): /protect/threats/web. Web threat protection settings (protection bundle) plus the
 * site-access policies that govern navigation (the policy lens this page replaces).
 */

/**
 * One control for the two stored settings: Off = web.enabled false (the mode is kept for when it is turned back on);
 * Monitor / Warn / Block = web.enabled true + web.mode (the three modes the server accepts).
 */
type WebLevel = 'off' | 'monitor' | 'warn' | 'block';
const WEB_LEVELS: Array<[WebLevel, string, string]> = [
  ['off', 'Off', 'Browsers do not check sites against threat lists.'],
  ['monitor', 'Monitor', 'Nothing is stopped. Visits are recorded as events so you can see what would be blocked.'],
  ['warn', 'Warn', 'A warning page explains the risk; people can choose “Continue anyway” (remembered for that site for the browser session).'],
  ['block', 'Block', 'The page is stopped and a Ward block page names the category and your support contact.'],
];
export const webLevel = (w: { enabled: boolean; mode: string }): WebLevel => (w.enabled ? (w.mode as WebLevel) : 'off');
export const fromWebLevel = (level: WebLevel, mode: string) => (level === 'off' ? { enabled: false, mode } : { enabled: true, mode: level });
const sameSet = (a: string[], b: string[]) => a.length === b.length && a.every((x) => b.includes(x));

export function WebProtection() {
  const q = useProtectionSettings();
  return (
    <>
      <PageHead
        title="Web protection"
        sub="Stops navigation to known malicious sites — malware, phishing, credential theft and more — in every enrolled browser, using threat intelligence feeds and your organisation’s own lists. Site-access policies below decide which applications can be used."
      />
      <Q q={q}>{(d) => <WebForm d={d} />}</Q>
      <SiteAccessPolicies />
    </>
  );
}

function WebForm({ d }: { d: ProtectionSettingsResponse }) {
  const can = useCan();
  const writable = can('settings:write');
  const [w, setW] = useState(d.settings.web as { enabled: boolean; mode: string; categories: string[]; allowDomains: string[] });
  const [allow, setAllow] = useState((d.settings.web.allowDomains as string[]).join('\n'));
  const [saved, setSaved] = useState<SaveResult | null>(null);
  const save = useSaveProtection(setSaved);
  const upd = (p: Partial<typeof w>) => { setW({ ...w, ...p }); setSaved(null); };
  const stats = d.bundle?.stats;
  const noData = !threatOff(d) && w.enabled && d.bundle !== null && (stats?.webDomains ?? 0) === 0;
  // Feed coverage per category (Threat protection → Threat intel). Absent on servers without it.
  const intel = useApi<{ coverage: Record<string, Record<string, number>>; mode: string }>('/threat/status', { retry: false });
  const coverage = intel.data?.coverage;
  const feedCount = (c: string) => Object.values(coverage?.[c] ?? {}).reduce((a, b) => a + b, 0);

  const off = threatOff(d);
  const level = webLevel(w);
  const isDefault = sameSet(w.categories, d.defaults.web.categories as string[]);
  return (
    <>
      {off && <ThreatOffBanner />}
      <fieldset className="m-0 p-0 border-0" disabled={!writable}>
        <Section title="Protection">
          <p className="sub m-0 mt-12 mb-8">Protect browsers from malicious websites{off && NOT_APPLIED}</p>
          <div className="seg" role="group" aria-label="Web protection mode" data-testid="web-mode">
            {WEB_LEVELS.map(([v, label, explain]) => (
              <button key={v} type="button" title={explain} aria-pressed={level === v} onClick={() => upd(fromWebLevel(v, w.mode))} aria-describedby="web-mode-explain" data-testid={`web-mode-${v}`}>{label}</button>
            ))}
          </div>
          <p className="explain m-0 mt-8" id="web-mode-explain">{WEB_LEVELS.find((x) => x[0] === level)![2]}</p>
        </Section>
        <Section title="Categories" aside="what counts as a threat">
          <p className="m-0 mt-12" data-testid="web-categories-summary">
            <b>{isDefault ? 'Default categories' : `Custom: ${w.categories.length} of ${d.categories.security.length} categories`}</b>
            <span className="sub"> — {w.categories.length ? w.categories.map(categoryLabel).join(', ') : 'none (nothing is checked)'}</span>
          </p>
          <details open={!isDefault || undefined}>
            <summary className="sub">Customize</summary>
          {coverage && (
            <p className="explain" data-testid="web-coverage-note">
              <b>feed data</b>: an enabled threat intelligence feed provides domains for this category. <b>your lists only</b>: no enabled feed covers it, so it blocks only the entries of your organisation’s deny lists (free feeds currently cover phishing and cryptomining; malware, credential theft and command-and-control need a licensed feed or your own lists).
            </p>
          )}
          <div className="stack-8 mt-12">
            {d.categories.security.map((c) => (
              <div key={c}>
                <label className="check">
                  <input type="checkbox" checked={w.categories.includes(c)} disabled={!w.enabled} aria-describedby={`web-category-${c}-explain`}
                    onChange={(e) => upd({ categories: e.target.checked ? [...w.categories, c] : w.categories.filter((x) => x !== c) })} data-testid={`web-category-${c}`} />
                  <b>{categoryLabel(c)}</b>
                </label>
                <span id={`web-category-${c}-explain`}>
                  {(d.defaults.web.categories as string[]).includes(c) && <Tag className="ml-8">default</Tag>}
                  <span className="sub"> — {CATEGORY_INFO[c]?.[1]}</span>
                  {stats?.webByCategory?.[c] !== undefined && <span className="sub"> ({fmtNum(stats.webByCategory[c])} domains)</span>}
                  {coverage && (feedCount(c) > 0
                    ? <Tag tone="allow" className="ml-8" data-testid={`web-coverage-${c}`}>feed data</Tag>
                    : <Tag className="ml-8" data-testid={`web-coverage-${c}`}>your lists only</Tag>)}
                </span>
              </div>
            ))}
          </div>
          </details>
        </Section>
        <Section title="Always allow" aside="wins over feeds and deny lists">
          <label className="field mt-12">Domains <span className="hint">one per line; subdomains included. Organisation allow lists under Threat intel apply as well.</span>
            <textarea rows={4} value={allow} onChange={(e) => { setAllow(e.target.value); setSaved(null); }} placeholder="intranet.example.com" data-testid="web-allow-domains" />
          </label>
        </Section>
        <details className="mt-16">
          <summary><b>How it works</b> <span className="sub">where the lists come from, what is reported, expected impact</span></summary>
          <p className="explain">Ward compiles one list per organisation from the enabled threat intelligence feeds (free and open feeds whose licence allows this use) and your organisation’s deny and allow lists (Threat protection → Threat intel). Browsers receive only the domains of the categories enabled here — never the feeds themselves — and check sites locally: no browsing history is sent to Ward. Blocks, warnings and continued visits are reported as events with the category and the site’s host name only.</p>
          <p className="explain"><b>Expected impact:</b> legitimate sites are rarely listed. If one is, add it to “Always allow” — the change reaches browsers within minutes. Use <i>Monitor</i> first if you want to see what would be blocked.</p>
        </details>
      </fieldset>
      {noData && <Notice tone="warn" role="note" data-testid="web-no-data">No threat intelligence is available yet, so nothing is blocked. Enable feeds or add a deny list under Threat intelligence.</Notice>}
      {stats?.webTruncated && <Notice tone="warn" role="note">The list was capped to keep the download small for browsers; the most reliable entries are kept.</Notice>}
      <SaveBar writable={writable} busy={save.isPending} error={save.error} saved={saved} onSave={() => save.mutate({ web: { ...w, allowDomains: lines(allow) } })} />
    </>
  );
}

/** Policies that govern navigation (kept from the former Protect → Web policy lens). */
function SiteAccessPolicies() {
  const can = useCan();
  return (
    <PolicyLens
      title="Site-access policies"
      aside={can('policies:write') && <Link to="/protect/policies/new?template=block-unsanctioned">New policy from a template</Link>}
      match={(c) => !!c.actions?.includes('navigate') || (!c.data && !c.actions)}
      emptyTitle="No policies decide which sites can be used"
    >
      <p className="explain">For hard URL blocking on managed devices, Edge’s URLBlocklist policy remains the strongest control.</p>
    </PolicyLens>
  );
}
